import {
  Article,
  ArticleAction,
  ArticleImage,
  ArticleList,
  ArticleSubtitle,
  ArticleTitle,
} from "@babackapp/customer-ui/components/article";
import { Button } from "@babackapp/customer-ui/components/button";
import {
  Stepper,
  StepperDivider,
  StepperHighlight,
  StepperItem,
} from "@babackapp/customer-ui/components/stepper";
import { cn } from "@babackapp/customer-ui/lib/utils";
import {
  InfoIcon,
  Minus,
  Plus,
  SettingsIcon,
  ShoppingCartIcon,
  Trash2,
  WalletIcon,
  XIcon,
} from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import { Link, useLocation, useNavigate, useOutlet } from "react-router-dom";
import { useLineItemLayout } from "../../hooks/use-line-item-layout";
import { SummaryToolbar } from "./summary-toolbar";

export function LineItemLayout() {
  const { pathname } = useLocation();
  const parts = pathname.split("/");
  const group = parts[parts.length - 3];
  const step = parts[parts.length - 1];
  const outlet = useOutlet();

  return (
    <div className="w-full flex flex-col flex-1">
      <AnimatePresence mode="wait">
        {group === "line-item" && step !== "confirmation" && (
          <motion.div
            initial={{ y: -50, opacity: 0 }}
            animate={{ y: 0, opacity: 1, transition: { type: "spring" } }}
            exit={{ y: -50, opacity: 0 }}
            className={cn("absolute top-8 left-8 right-6")}
          >
            <LineItemStepper />
          </motion.div>
        )}
      </AnimatePresence>
      <AnimatePresence mode="popLayout">
        {!["line-item", "confirmation"].includes(step) ? (
          <LineItemBar />
        ) : (
          <SummaryToolbar />
        )}
      </AnimatePresence>
      <AnimatePresence mode="wait" propagate>
        <motion.div key={pathname} className="h-full">
          {outlet}
        </motion.div>
      </AnimatePresence>
    </div>
  );
}

function LineItemBar() {
  const { lineItem, quantity, setQuantity, returnableQty, removeItem, lineItemId } = useLineItemLayout();

  if (!lineItem) return <></>;

  const options = [lineItem.variant?.option1, lineItem.variant?.option2, lineItem.variant?.option3]
    .filter(Boolean)
    .filter((o) => o !== "Default Title")
    .join(" • ");

  return (
    <ArticleList
      className="absolute bottom-4 left-4 w-auto right-4 drop-shadow-md overflow-hidden bg-background"
      variants={{}}
      initial={{ y: 100 }}
      animate={{ y: 0, transition: { type: "spring" } }}
      exit={{ y: 100 }}
    >
      <Article>
        <ArticleImage src={lineItem.image ?? undefined} layoutId={`img-${lineItemId}`} />
        <ArticleTitle>{lineItem.title}</ArticleTitle>
        <ArticleSubtitle>{options || null}</ArticleSubtitle>
        <ArticleAction autoHide={false}>
          {returnableQty > 1 ? (
            <div className="flex items-center gap-1 border rounded-lg h-8 px-1 text-xs font-mono font-medium tabular-nums">
              <button
                type="button"
                onClick={() => setQuantity(Math.max(1, quantity - 1))}
                className="px-1 text-muted-foreground hover:text-foreground"
              >
                <Minus className="size-3" />
              </button>
              <span className="min-w-4 text-center">{quantity}</span>
              <button
                type="button"
                onClick={() => setQuantity(Math.min(returnableQty, quantity + 1))}
                className="px-1 text-muted-foreground hover:text-foreground"
              >
                <Plus className="size-3" />
              </button>
            </div>
          ) : (
            <span className="text-xs font-mono font-medium tabular-nums text-muted-foreground">×1</span>
          )}
          <Button size="icon" variant="outline" type="button" onClick={removeItem}>
            <Trash2 />
          </Button>
        </ArticleAction>
      </Article>
    </ArticleList>
  );
}

function LineItemStepper() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const step = pathname.split("/").pop();

  const stepState = (match: string, after: string[]) =>
    step === match ? "present" : after.includes(step!) ? "past" : "future";

  return (
    <div className="flex gap-2 items-center -ml-0">
      <Link to="/return-widget/return-requests" className="mr-4">
        <XIcon className="size-5" />
      </Link>
      <Stepper>
        <StepperItem
          state={stepState("line-item", ["reason", "resolution", "resolution-options"])}
          onClick={() => navigate("")}
        >
          {step === "line-item" && <StepperHighlight />}
          <ShoppingCartIcon />
          <span>Article</span>
        </StepperItem>
        <StepperDivider />
        <StepperItem
          state={stepState("reason", ["resolution", "resolution-options"])}
          onClick={() => navigate("../reason")}
        >
          {step === "reason" && <StepperHighlight />}
          <InfoIcon />
          <span>Raison</span>
        </StepperItem>
        <StepperDivider />
        <StepperItem
          state={stepState("resolution", ["resolution-options"])}
          onClick={() => navigate("../resolution")}
        >
          {step === "resolution" && <StepperHighlight />}
          <WalletIcon />
          <span>Resolution</span>
        </StepperItem>
        <StepperDivider />
        <StepperItem
          state={stepState("resolution-options", [])}
          onClick={() => navigate("../resolution-options")}
        >
          {step === "resolution-options" && <StepperHighlight />}
          <SettingsIcon />
          <span>Options</span>
        </StepperItem>
      </Stepper>
    </div>
  );
}
